import { useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FileText, Eye, Upload, Loader2, CheckCircle2, Award, ShieldCheck } from 'lucide-react'
import { PassportUploadCard } from '../PassportUploadCard'
import { PortalOutletChangeCard } from '../PortalOutletChangeCard'
import { PortalOutletDeclarationCard } from '../PortalOutletDeclarationCard'
import { PortalEmptyState } from '../ui/PortalEmptyState'
import type { JournalistProfileState } from './useJournalistProfile'

const ACCRED_COLORS: Record<string, string> = {
  active:  'bg-[hsl(var(--success)/0.15)] text-success',
  pending: 'bg-[hsl(var(--warning)/0.18)] text-warning',
  rejected:'bg-[hsl(var(--destructive)/0.12)] text-destructive',
  revoked: 'bg-muted text-muted-foreground',
}

const DOCS: { key: 'doc_national_id' | 'doc_press_card' | 'doc_assignment'; label: string; hint: string }[] = [
  { key: 'doc_national_id', label: 'National ID', hint: 'Government-issued photo ID' },
  { key: 'doc_press_card',  label: 'Press Card',  hint: 'Current press credential issued by your outlet' },
  { key: 'doc_assignment',  label: 'Assignment Letter', hint: 'Letter from your editor confirming this assignment' },
]

export function ProfileCredentialsTab({ state }: { state: JournalistProfileState }) {
  const { profile, accreditations, authUserId, uploadingDoc, uploadDoc, viewDoc, editing, form, setForm } = state
  const docInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const done = DOCS.filter(d => !!(profile as any)?.[d.key]).length
  const pct = (done / DOCS.length) * 100

  return (
    <div className="space-y-5">
      <PortalOutletDeclarationCard />
      <PortalOutletChangeCard />

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-muted-foreground" /> Identity verification
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5 text-justify">
            The ID or passport number on your National ID document above. Each journalist may only hold one account — this number is checked against every other profile on the platform.
          </p>
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="space-y-1.5 max-w-xs">
              <Label>National ID / passport number</Label>
              <Input value={form.national_id_number} onChange={e => setForm(f => ({ ...f, national_id_number: e.target.value }))} placeholder="e.g. A01234567" />
            </div>
          ) : (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">National ID / passport number</div>
              <div className="text-sm mt-0.5 font-mono">{profile?.national_id_number || <span className="text-muted-foreground font-sans">Not provided</span>}</div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">My Documents</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5 text-justify">
            Uploaded documents are shared with Communications Officers when you apply for accreditation. They are stored securely and are not publicly accessible.
          </p>
          <div className="pt-2">
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
              <span>Completeness</span>
              <span>{done} of {DOCS.length}</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-success rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {DOCS.map(({ key, label, hint }) => {
            const stored = (profile as any)?.[key] as string | undefined
            const isUploading = uploadingDoc === key
            return (
              <div key={key} className="flex items-start gap-4 p-3 rounded-lg border border-border transition-colors hover:border-accent/50">
                <FileText className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium">{label}</span>
                    {stored ? (
                      <span className="flex items-center gap-1 text-[10px] text-success">
                        <CheckCircle2 className="w-3 h-3" /> Uploaded
                      </span>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">Not uploaded</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground text-justify">{hint}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {stored && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 gap-1 text-xs" onClick={() => viewDoc(stored)}>
                      <Eye className="w-3 h-3" /> View
                    </Button>
                  )}
                  <Button
                    variant="outline" size="sm" className="h-7 px-2 gap-1 text-xs"
                    disabled={isUploading}
                    onClick={() => docInputRefs.current[key]?.click()}
                  >
                    {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    {isUploading ? 'Uploading…' : stored ? 'Replace' : 'Upload'}
                  </Button>
                  <input
                    ref={el => { docInputRefs.current[key] = el }}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadDoc(key, f); e.target.value = '' }}
                  />
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>

      {authUserId && <PassportUploadCard journalistId={authUserId} />}

      <Card>
        <CardHeader><CardTitle className="text-base">Accreditation History</CardTitle></CardHeader>
        <CardContent>
          {(accreditations ?? []).length === 0 ? (
            <PortalEmptyState icon={Award} message="No accreditation history yet." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Organisation</th>
                    <th className="pb-2 font-medium text-muted-foreground">Status</th>
                    <th className="pb-2 font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(accreditations ?? []).map((acc: any) => (
                    <tr key={acc.id} className="border-b last:border-0">
                      <td className="py-2 font-medium">{acc.organizations?.name}</td>
                      <td className="py-2">
                        <Badge className={`text-[10px] ${ACCRED_COLORS[acc.status] ?? ACCRED_COLORS.pending}`}>
                          {acc.status}
                        </Badge>
                      </td>
                      <td className="py-2 text-muted-foreground">
                        {acc.accredited_at ? new Date(acc.accredited_at).toLocaleDateString() : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
